import { validatePriority, validateDifficulty, validateEstimatedMinutes, validateEnergyLevel, validateTaskStatus, validateISODateTimeFormat, validateISODateFormat, validateRecurrenceFrequency, validateTimeFormat, } from '../utils/validation';
const show = (value) => {
    if (typeof value === 'string')
        return JSON.stringify(value);
    if (value === undefined)
        return 'undefined';
    if (value === null)
        return 'null';
    if (Array.isArray(value))
        return `array(${value.length})`;
    if (typeof value === 'object')
        return 'object';
    return String(value);
};
/**
 * Read any object as a bag of unknown fields.
 *
 * The cast is the whole point of this module: callers include the typed UI
 * form (an `interface`, which TypeScript will not hand to an index-signature
 * parameter) *and* untyped JSON from an import or a tool call. Both are
 * checked by the same code, so the entry has to widen once, here, rather than
 * at every call site.
 */
const asBag = (input) => input;
/**
 * Every problem with this input, not just the first.
 *
 * Returning all of them matters when the caller is an AI assistant: one
 * round trip per mistake turns a three-field error into three tool calls.
 */
export function validateTaskInput(source, { requireTitle = false, requireProject = false } = {}) {
    const input = asBag(source);
    const problems = [];
    const has = (k) => input[k] !== undefined && input[k] !== null;
    if (requireTitle && (!has('title') || String(input.title).trim() === '')) {
        problems.push({
            field: 'title', received: show(input.title),
            expected: 'a non-empty string',
        });
    }
    if (requireProject && !has('project_id')) {
        problems.push({
            field: 'project_id', received: show(input.project_id),
            expected: 'the id of an existing project — resolve a project NAME to its id first',
        });
    }
    if (has('priority') && !validatePriority(input.priority)) {
        problems.push({
            field: 'priority', received: show(input.priority),
            expected: 'an integer 1-5, where 5 is highest',
            repair: 'clamped into 1-5',
        });
    }
    if (has('difficulty') && !validateDifficulty(input.difficulty)) {
        problems.push({
            field: 'difficulty', received: show(input.difficulty),
            expected: 'an integer 1-5',
            repair: 'clamped into 1-5',
        });
    }
    if (has('estimated_minutes') && !validateEstimatedMinutes(input.estimated_minutes)) {
        problems.push({
            field: 'estimated_minutes', received: show(input.estimated_minutes),
            expected: 'a positive whole number of minutes',
            repair: 'dropped, falling back to the 60-minute default',
        });
    }
    if (has('energy_level') && !validateEnergyLevel(input.energy_level)) {
        problems.push({
            field: 'energy_level', received: show(input.energy_level),
            expected: "one of 'low', 'medium', 'high'",
            repair: "dropped, falling back to 'medium'",
        });
    }
    if (has('status') && !validateTaskStatus(input.status)) {
        problems.push({
            field: 'status', received: show(input.status),
            expected: "one of 'todo', 'in_progress', 'completed', 'blocked'",
            repair: "dropped, falling back to 'todo'",
        });
    }
    if (has('deadline') && !validateISODateTimeFormat(input.deadline)
        && !validateISODateFormat(input.deadline)) {
        problems.push({
            field: 'deadline', received: show(input.deadline),
            expected: 'YYYY-MM-DD or an ISO datetime',
            repair: 'dropped',
        });
    }
    if (has('scheduled_for') && !validateISODateTimeFormat(input.scheduled_for)
        && !validateISODateFormat(input.scheduled_for)) {
        problems.push({
            field: 'scheduled_for', received: show(input.scheduled_for),
            expected: 'YYYY-MM-DD or an ISO datetime — note this pins a FIXED time, '
                + 'and is not the same as `deadline`',
            repair: 'dropped',
        });
    }
    for (const key of ['tags', 'dependencies']) {
        if (has(key) && !Array.isArray(input[key])) {
            problems.push({
                field: key, received: show(input[key]),
                expected: 'an array of strings',
                repair: 'dropped',
            });
        }
    }
    const recurrence = input.recurrence;
    if (recurrence && typeof recurrence === 'object') {
        const freq = recurrence.frequency;
        if (freq !== undefined && freq !== null && !validateRecurrenceFrequency(freq)) {
            problems.push({
                field: 'recurrence.frequency', received: show(freq),
                expected: "one of 'daily', 'weekly', 'monthly'",
            });
        }
        const time = recurrence.time_of_day;
        if (time !== undefined && time !== null && time !== ''
            && !validateTimeFormat(time)) {
            problems.push({
                field: 'recurrence.time_of_day', received: show(time),
                expected: 'HH:MM in 24-hour form',
            });
        }
        if (recurrence.days_of_week !== undefined && !Array.isArray(recurrence.days_of_week)) {
            problems.push({
                field: 'recurrence.days_of_week', received: show(recurrence.days_of_week),
                expected: "an array of weekday names, e.g. ['monday','wednesday']",
            });
        }
        const dom = recurrence.day_of_month;
        if (dom !== undefined && dom !== null
            && (!Number.isInteger(dom) || dom < 1 || dom > 31)) {
            problems.push({
                field: 'recurrence.day_of_month', received: show(dom),
                expected: 'an integer 1-31 (29-31 fall on the last day of shorter months)',
            });
        }
    }
    return {
        valid: problems.length === 0,
        problems,
        message: problems
            .map((p) => `${p.field}: got ${p.received}, expected ${p.expected}`)
            .join('; '),
    };
}
/**
 * The same input with unusable values repaired or removed.
 *
 * Repair beats rejection for bulk paths: an import of 400 tasks where three
 * carry a bad priority should land 400 tasks, not zero. Callers that want
 * strictness run `validateTaskInput` first and refuse on `!valid`.
 *
 * Generic in the input type so a typed caller gets its own type back and does
 * not have to cast on the way out — the UI passes its form shape through this
 * and keeps every field it declared.
 */
export function coerceTaskInput(source) {
    const out = { ...asBag(source) };
    const clampInt = (value, lo, hi) => {
        const n = typeof value === 'number' ? value : Number(value);
        if (!Number.isFinite(n))
            return undefined;
        return Math.min(hi, Math.max(lo, Math.round(n)));
    };
    if (out.priority !== undefined && !validatePriority(out.priority)) {
        const fixed = clampInt(out.priority, 1, 5);
        if (fixed === undefined)
            delete out.priority;
        else
            out.priority = fixed;
    }
    if (out.difficulty !== undefined && !validateDifficulty(out.difficulty)) {
        const fixed = clampInt(out.difficulty, 1, 5);
        if (fixed === undefined)
            delete out.difficulty;
        else
            out.difficulty = fixed;
    }
    if (out.estimated_minutes !== undefined
        && !validateEstimatedMinutes(out.estimated_minutes)) {
        // No clamping to 1: a negative or non-numeric estimate is a mistake, not
        // a very short task, and the 60-minute default is the honest fallback.
        delete out.estimated_minutes;
    }
    if (out.energy_level !== undefined && !validateEnergyLevel(out.energy_level)) {
        delete out.energy_level;
    }
    if (out.status !== undefined && !validateTaskStatus(out.status)) {
        delete out.status;
    }
    for (const key of ['deadline', 'scheduled_for']) {
        const v = out[key];
        if (v !== undefined && v !== null
            && !validateISODateTimeFormat(v) && !validateISODateFormat(v)) {
            delete out[key];
        }
    }
    for (const key of ['tags', 'dependencies']) {
        if (out[key] !== undefined && !Array.isArray(out[key]))
            delete out[key];
    }
    return out;
}
//# sourceMappingURL=task-input.js.map